import React, { FC, useMemo } from 'react';
import {
    useTranslate,
    IResourceComponentsProps,
    HttpError,
    useNavigation,
} from '@refinedev/core';
import { Table } from 'antd';
import { TextField, getDefaultSortOrder, useTable } from '@refinedev/antd';
import { IApplication, IRecordFilterVariables } from 'interfaces';
import { useParams } from 'react-router-dom';
import { InternalLayout } from 'layouts/InternalLayout';
import TablePagination from 'components/ui/table-pagination';
import styles from '../list/list.module.scss';

export const PharmasolShow: FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const { list } = useNavigation();

    const { id: dictName } = useParams();

    const {
        tableProps,
        sorters: sorter,
        current,
        setCurrent,
        pageSize,
        setPageSize,
        tableQueryResult,
    } = useTable<IApplication, HttpError, IRecordFilterVariables>({
        resource: `pharmasol/dictionaries/sync/${dictName}`,
    });

    const columns = useMemo(
        () => tableProps.dataSource?.[0] ?? {},
        [tableProps.dataSource]
    );

    const { pagination: rawPagination, ...restTableProps } = tableProps;
    const pagination =
        rawPagination === false ? undefined : rawPagination ?? undefined;

    const totalItems =
        tableQueryResult?.data?.total ??
        pagination?.total ??
        tableProps?.dataSource?.length ??
        0;
    const currentPage = current ?? pagination?.current ?? 1;
    const resolvedPageSize = pageSize ?? pagination?.pageSize ?? 10;

    const defaultPageSizeOptions = ['10', '25', '50', '100'];
    const rawPageSizeOptions = (
        pagination?.pageSizeOptions ?? defaultPageSizeOptions
    ).map(option => option.toString());

    const uniquePageSizeOptions = rawPageSizeOptions.reduce<string[]>(
        (acc, current) => {
            if (!acc.includes(current)) acc.push(current);
            return acc;
        },
        []
    );

    const orderedPageSizeOptions = defaultPageSizeOptions.filter(option =>
        uniquePageSizeOptions.includes(option)
    );

    uniquePageSizeOptions.forEach(option => {
        if (!orderedPageSizeOptions.includes(option)) {
            orderedPageSizeOptions.push(option);
        }
    });

    if (!orderedPageSizeOptions.includes(resolvedPageSize.toString())) {
        orderedPageSizeOptions.push(resolvedPageSize.toString());
    }

    const handlePageChange = (page: number) => {
        setCurrent?.(page);
    };

    const handlePageSizeChange = (value: number) => {
        if (Number.isNaN(value)) return;
        setPageSize?.(value);
        setCurrent?.(1);
    };

    const renderColumn = (column: string) => {
        return (
            <Table.Column
                dataIndex={column}
                key={column}
                title={t(`pharmasol.fields.${column}`, column)}
                render={value => <TextField value={value} />}
                defaultSortOrder={getDefaultSortOrder(column, sorter)}
                sorter
            />
        );
    };

    return (
        <InternalLayout
            hideSidebar
            title={t('pharmasol.fields.show')}
            contentClassName={styles.pageWrapper}
            onBack={() => list('dictionaries')}
            noPadding
        >
            <div className={styles.tableWrapper}>
                <Table
                    {...restTableProps}
                    pagination={false}
                    rowKey="id"
                    className={styles.table}
                    scroll={{ x: 500 }}
                >
                    {Object?.keys(columns).map(column => renderColumn(column))}
                </Table>
            </div>
            {totalItems ? (
                <TablePagination
                    currentPage={currentPage}
                    pageSize={resolvedPageSize}
                    totalItems={totalItems}
                    pageSizeOptions={orderedPageSizeOptions}
                    onChangePage={handlePageChange}
                    onChangePageSize={handlePageSizeChange}
                    previousLabel={t('applications.pagination.previous')}
                    nextLabel={t('applications.pagination.next')}
                    showDocumentsLabel={t(
                        'applications.pagination.show_documents'
                    )}
                    rangeLabel={(start, end, total) =>
                        `${start}-${end} ${t(
                            'applications.pagination.of_total'
                        )} ${total}`
                    }
                    className={styles.pagination}
                />
            ) : null}
        </InternalLayout>
    );
};
